<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title ref="ticketTitleRef">Ticket Manager: {{ showName }}</v-toolbar-title>
      <v-spacer />
      <span style="transform:translateY(2px)">Performance Time: {{ performanceDate }}</span>
      <v-spacer />
      <div ref="spacerRef">
      </div>
      <v-btn icon @click="exportList">
        <v-icon color="success">fal fa-file-spreadsheet</v-icon>
      </v-btn>
    </v-toolbar>
    <v-toolbar>
      <v-text-field v-model="nameFilter" label="Filter Name/Banner ID/Email" outlined dense hide-details clearable class="mr-2" style="max-width:300px" />
      <v-select v-model="statusFilter" :items="statusFilterOptions" label="Filter Status" outlined dense hide-details multiple chips small-chips deletable-chips class="mr-2" style="max-width:300px" />
      <v-select v-model="ticketTypeFilter" :items="ticketTypes" label="Filter Ticket Types" outlined dense hide-details multiple chips small-chips deletable-chips class="mr-2" style="max-width:300px" />
    </v-toolbar>
    <v-data-table :items="filteredTickets" :headers="headers">
      <template #item.tickets="{ item }">
        <v-chip v-for="{ label, tickets } of item.tickets" :key="label" label outlined class="mr-2">{{ label }} ({{ tickets }})</v-chip>
      </template>
      <template #item.status="{ item }">
        <v-menu>
          <template #activator="{ on }">
            <v-btn v-on="on">
              {{ item.status }}
              <v-icon right>fal fa-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-subheader>Change status to</v-subheader>
            <v-list-item v-for="status in statusOptions" :key="status" :disabled="status === item.status" @click="updateStatus(item, status)">
              <v-list-item-title>{{ status }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template #item.action="{ item }">
        <v-btn v-if="!['Checked-In','Cancelled'].includes(item.status)" @click="checkIn(item)">Check In</v-btn>
      </template>
    </v-data-table>
  </v-card>
</template>
<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import { saveAs } from 'file-saver'

export default {
  setup (props, { root, $route }) {
    const performanceId = computed(() => root.$route.params.id)
    const showName = ref('')
    const performanceDate = ref('')
    const nameFilter = ref('')
    const statusFilter = ref([])
    const statusFilterOptions = ref([])
    const statusOptions = ref(['Paid', 'Reserved', 'Cancelled', 'Checked-In'])
    const ticketTypeFilter = ref([])
    const ticketTypes = ref([])
    const tickets = ref([])
    const filteredTickets = computed(() => tickets.value.filter(({ status, tickets, last, first, bannerId, email }) => {
      if (statusFilter.value && statusFilter.value.length > 0 && !statusFilter.value.includes(status)) return false
      if (ticketTypeFilter.value && ticketTypeFilter.value.length > 0 && tickets.filter(({ label }) => ticketTypeFilter.value.includes(label)).length === 0) return false
      if (nameFilter.value) {
        const regex = new RegExp(nameFilter.value, 'ig')
        if (!regex.test(`${last}, ${first} ${last} ${email} ${bannerId}`)) return false
      }
      return true
    }))
    const headers = ref([
      { text: 'Banner ID', value: 'bannerId' },
      { text: 'Name', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Ticket Count', value: 'total' },
      { text: 'Ticket Type(s) Chosen', value: 'tickets' },
      { text: 'Status', value: 'status' },
      { text: '', value: 'action' }
    ])
    const ticketTitleRef = ref(null)
    const spacerRef = ref(null)

    onMounted(async () => {
      const { data: showData } = await root.$feathers.service('theatre/show').find({ query: { 'performances._id': performanceId.value } })
      if (showData.length === 1) {
        showName.value = showData[0].name
        const rec = showData[0].performances.find(({ _id }) => _id === performanceId.value)
        if (rec) {
          const dt = new Date(rec.date)
          const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
          const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
          performanceDate.value = `${date} at ${time}`
        }
      }
      const aggregate = [
        { $match: { 'tickets.performance': root.$route.params.id } },
        { $addFields: { tickets: { $filter: { input: '$tickets', as: 'ticket', cond: { $eq: ['$$ticket.performance', { $toObjectId: root.$route.params.id }] } } } } },
        { $addFields: {
          total: { $sum: '$tickets.tickets' },
          name: { $concat: ['$first', ' ', '$last'] },
          ticStatus: { $reduce: {
            input: '$tickets',
            initialValue: null,
            in: { $cond: {
              if: { $eq: ['$$value', null] },
              then: '$$this.status',
              else: {
                $cond: {
                  if: { $lt: ['$$this.status', '$$value'] },
                  then: '$$this.status',
                  else: '$$value'
                }
              }
            } }
          } }
        } },
        { $sort: { last: 1, first: 1 } }
      ]
      const { data } = await root.$feathers.service('theatre/ticket').find({ query: { aggregate } })
      tickets.value = data.map((row) => {
        if (row.ticStatus) row.status = row.ticStatus
        return row
      })
      for (const { status, tickets: tics } of tickets.value) {
        if (!statusFilterOptions.value.includes(status)) statusFilterOptions.value.push(status)
        for (const { tickets: t, label } of tics) {
          if (t === 0) continue
          if (!ticketTypes.value.includes(label)) {
            ticketTypes.value.push(label)
          }
        }
      }
      spacerRef.value.style.width = (ticketTitleRef.value.offsetWidth - 48) + 'px'
    })

    async function updateStatus (item, status) {
      await root.$feathers.service('theatre/ticket').patch(item._id, { 'tickets.$[tic].status': status }, { query: { arrayFilters: [{ 'tic.performance': performanceId.value }] } })
      const index = tickets.value.findIndex(({ _id }) => _id === item._id)
      if (index >= 0) {
        tickets.value.splice(index, 1, { ...tickets.value[index], status })
      }
    }

    async function checkIn (item) {
      updateStatus(item, 'Checked-In')
    }

    async function exportList () {
      const header = [
        { header: 'Banner ID', key: 'bannerId', width: 20 },
        { header: 'Name', key: 'name', width: 30 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Ticket Count', key: 'total', width: 15 },
        { header: 'Status', key: 'status', width: 20 }
      ]
      // Go through the ticket types (limited to only those in the filter if they are filtered)
      if (ticketTypeFilter.value.length > 0) {
        ticketTypeFilter.value.forEach((text) => {
          header.push({ header: text, key: text, width: 30 })
        })
      } else {
        ticketTypes.value.forEach((text) => {
          header.push({ header: text, key: text, width: 30 })
        })
      }
      const filename = `Tickets for ${showName.value} on ${performanceDate.value}.xlsx`
      const rows = filteredTickets.value.map((row) => {
        const tickets = {}
        if (ticketTypeFilter.value.length > 0) {
          ticketTypeFilter.value.forEach((text) => {
            const rec = row.tickets.find(({ label }) => label === text)
            if (rec) {
              tickets[text] = rec.tickets
            }
          })
        } else {
          ticketTypes.value.forEach((text) => {
            const rec = row.tickets.find(({ label }) => label === text)
            if (rec) {
              tickets[text] = rec.tickets
            }
          })
        }
        return { ...row, ...tickets }
      })
      const data = await root.$feathers.service('export/xlsx').create({ filename, header, rows })
      saveAs(new Blob([data.buffer]), filename)
    }

    return {
      performanceId,
      showName,
      performanceDate,
      nameFilter,
      statusFilter,
      statusFilterOptions,
      statusOptions,
      ticketTypeFilter,
      ticketTypes,
      tickets,
      filteredTickets,
      headers,
      ticketTitleRef,
      spacerRef,
      updateStatus,
      checkIn,
      exportList
    }
  }
}
</script>
